import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import StatusChip, { QNA_ANSWER_STATUS_CHIP } from '../../component/StatusChip'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, FORM_SUB_PANEL_SX } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import { formatDate } from '../../utils/dateDisplay'
import {
    applyQnaDraft,
    clearQnaDraft,
    createEditQnaDraftKey,
    createQnaDraftPayload,
    readQnaDraft,
    saveQnaDraft,
} from './qnaDraftUtils'

const QUESTION_IMAGE_PANEL_SX = FORM_PANEL_SX

const QUESTION_IMAGE_PREVIEW_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    minHeight: 240,
    height: 'auto',
    maxHeight: 520,
}

const QUESTION_IMAGE_PREVIEW_BUTTON_SX = {
    ...QUESTION_IMAGE_PREVIEW_SX,
    p: 0,
    appearance: 'none',
    cursor: 'zoom-in',
    transition: 'border-color 120ms ease, background-color 120ms ease',
    '&:hover': {
        borderColor: 'primary.main',
        backgroundColor: 'action.hover',
    },
    '&:focus-visible': {
        outline: '2px solid',
        outlineColor: 'primary.main',
        outlineOffset: 2,
    },
}

const QUESTION_IMAGE_DIALOG_TITLE_SX = {
    pr: 6,
    display: 'flex',
    alignItems: 'center',
    minHeight: 56,
}

const QUESTION_IMAGE_DIALOG_CLOSE_SX = {
    position: 'absolute',
    right: 8,
    top: 8,
}

const QUESTION_IMAGE_DIALOG_CONTENT_SX = {
    p: 0,
    bgcolor: 'background.default',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultData = () => ({})

const detailSectionSx = {
    ...FORM_PANEL_SX,
    p: 2,
    mb: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: 'none',
}

const detailFieldSx = {
    ...FORM_SUB_PANEL_SX,
    p: 1.25,
    minHeight: 72,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
}

const stretchMultilineTextFieldSx = {
    flex: 1,
    display: 'flex',
    '& .MuiInputBase-root': {
        flex: 1,
        alignItems: 'flex-start',
    },
    '& textarea': {
        height: '100% !important',
    },
}

const getDetailDisplayValue = (value) => {
    if (value === undefined || value === null || value === '') {
        return '-'
    }

    return value
}

const getUserId = (data) => data?.user?.userId ?? data?.userId

const getQnaTypeLabel = (type) => {
    switch (Number(type)) {
        case 0:
            return '제휴'
        case 1:
            return '기타'
        case 2:
            return '광고'
        case 3:
            return '대회'
        default:
            return '미설정'
    }
}

function DetailSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={detailSectionSx}>
            <Box sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {title}
                </Typography>
                {description && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                        {description}
                    </Typography>
                )}
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {children}
            </Box>
        </Paper>
    )
}

function DetailField({ label, value, onClick }) {
    const clickable = typeof onClick === 'function'

    return (
        <Box
            component={clickable ? 'button' : 'div'}
            type={clickable ? 'button' : undefined}
            sx={{
                ...detailFieldSx,
                ...(clickable ? {
                    width: '100%',
                    appearance: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'left',
                    font: 'inherit',
                    cursor: 'pointer',
                    '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                    },
                    '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                    },
                } : {}),
            }}
            onClick={onClick}
            aria-label={clickable ? `${label} 복사` : undefined}
        >
            <Typography variant="caption" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 700 }}>
                {label}
            </Typography>
            {React.isValidElement(value) ? value : (
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700, wordBreak: 'break-word' }}>
                    {getDetailDisplayValue(value)}
                </Typography>
            )}
        </Box>
    )
}

const copyTextToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return
    }

    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.setAttribute('readonly', '')
    textArea.style.position = 'absolute'
    textArea.style.left = '-9999px'
    document.body.appendChild(textArea)
    textArea.select()

    const copied = document.execCommand('copy')
    document.body.removeChild(textArea)

    if (!copied) {
        throw new Error('copy_failed')
    }
}

// 문의 첨부 이미지를 고정 폭 카드 대신 화면 폭에 맞는 확인 패널로 보여준다.
function QuestionImagePreview({ imageSrc, description }) {
    const [openImageDialog, setOpenImageDialog] = useState(false)

    return (
        <>
            <Paper variant="outlined" sx={QUESTION_IMAGE_PANEL_SX}>
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        첨부 이미지
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {description}
                    </Typography>
                </Box>
                <Box
                    component="button"
                    type="button"
                    sx={QUESTION_IMAGE_PREVIEW_BUTTON_SX}
                    onClick={() => setOpenImageDialog(true)}
                    aria-label="첨부 이미지 크게 보기"
                >
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="문의 첨부 이미지"
                        sx={{
                            width: '100%',
                            maxHeight: 520,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                </Box>
            </Paper>
            <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={QUESTION_IMAGE_DIALOG_TITLE_SX}>
                    첨부 이미지
                    <IconButton
                        aria-label="닫기"
                        onClick={() => setOpenImageDialog(false)}
                        sx={QUESTION_IMAGE_DIALOG_CLOSE_SX}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={QUESTION_IMAGE_DIALOG_CONTENT_SX}>
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="문의 첨부 이미지 크게 보기"
                        sx={{
                            width: '100%',
                            maxHeight: 'calc(100vh - 160px)',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}

export default function Qna(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.qnaId
    const listCurPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchType: '', searchStatus: '' }
    const listSearch = location.search || ''
    const draftKey = createEditQnaDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [datas, setDatas] = useState(() => createDefaultData())
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const initialDraftSnapshotRef = useRef('')

    const resetDetailState = useCallback((errorMessage) => {
        setDatas(createDefaultData())
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage(errorMessage)
    }, [])

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 Q/A 문의 상세를 불러올 수 없습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/qna/getQna', { qnaId: id })
                // console.log('data: ', data.data)
                if (data.code === 1) {
                    const baseData = data.data || {}

                    if (!baseData.qnaId) {
                        resetDetailState('Q/A 문의 상세 정보를 찾을 수 없습니다.')
                        return
                    }

                    const storedDraft = readQnaDraft(draftKey)
                    const restoredDraft = applyQnaDraft({
                        baseData,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createQnaDraftPayload({
                        data: baseData,
                    }))

                    setDatas(restoredDraft.data)
                    setHasDetail(Boolean(baseData?.qnaId))
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || 'Q/A 문의 상세를 불러오지 못했습니다.'
                    resetDetailState(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = 'Q/A 문의 상세를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }
        getData()
    }, [draftKey, history, id, resetDetailState])

    useEffect(() => {
        if (!draftReady || !hasDetail || !datas?.qnaId) {
            return
        }

        // Q/A 문의 상세는 목록 query를 건드리지 않고 답변 draft만 sessionStorage에 보존한다.
        const nextDraft = createQnaDraftPayload({
            data: datas,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearQnaDraft(draftKey)
            return
        }

        saveQnaDraft(draftKey, nextDraft)
    }, [datas, draftKey, draftReady, hasDetail])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 답변 저장 후에도 목록의 페이지/검색 조건이 유지되도록 복귀 컨텍스트를 함께 보낸다.
    const moveToList = (snackbarState) => {
        clearQnaDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/qnas',
            search: listSearch,
            searchText,
            searchSelect,
            curPage: listCurPage,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setDatas({
            ...datas,
            [e.target.name]: e.target.value
        })
    }

    const handleCopyUserId = async () => {
        const copyValue = String(getUserId(datas) ?? '').trim()

        if (!copyValue) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('복사할 userId 값이 없습니다.')
            return
        }

        try {
            await copyTextToClipboard(copyValue)
            setOpenSnackbar(true)
            setSeveritySnackbar('success')
            setMessageSnackbar('userId를 복사했습니다.')
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('userId를 복사하지 못했습니다.')
        }
    }

    const updateQna = async () => {
        if (saving) {
            return
        }

        const trimmedReply = String(datas?.reply || '').trim()

        if (!trimmedReply) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('답변을 입력하세요.')
            return
        }

        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/qna/update', { qnaId: datas.qnaId, reply: trimmedReply })

            if (data.code === 1) {
                moveToList({ severity: 'success', message: 'Q/A 문의 답변을 저장했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('Q/A 문의 답변을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        updateQna()
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="Q/A 문의 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/qnas" search={listSearch} visible={false} searchText={searchText} searchSelect={searchSelect} curPage={listCurPage} onBackClick={() => clearQnaDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>Q/A 문의 답변</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    const replyHistory = Array.isArray(datas.qna_reply) ? datas.qna_reply : []

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>Q/A 문의 답변</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }} >
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                            <DetailSection
                                title="문의 정보"
                                description="작성자와 문의 상태를 확인합니다."
                            >
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12}>
                                        <DetailField
                                            label="답변 상태"
                                            value={<StatusChip status={datas.status ?? 0} config={QNA_ANSWER_STATUS_CHIP} />}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DetailField label="문의 유형" value={getQnaTypeLabel(datas.type)} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DetailField label="작성자" value={datas.user?.username} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DetailField label="userId" value={getUserId(datas)} onClick={handleCopyUserId} />
                                    </Grid>
                                </Grid>
                            </DetailSection>
                        </Grid>
                        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
                            <DetailSection
                                title="문의 내용"
                                description="사용자가 작성한 Q/A 문의 원문입니다."
                            >
                                <Grid container spacing={1.5} sx={{ flex: 1 }}>
                                    <Grid item xs={12}>
                                        <DetailField label="제목" value={datas.title} />
                                    </Grid>
                                    <Grid item xs={12} sx={{ display: 'flex', flex: 1 }}>
                                        <FormControl fullWidth sx={{ flex: 1, display: 'flex' }}>
                                            <TextField
                                                id="content"
                                                name="content"
                                                multiline
                                                minRows={8}
                                                value={datas.content || ''}
                                                size="small"
                                                InputProps={{ readOnly: true }}
                                                sx={stretchMultilineTextFieldSx}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </DetailSection>
                        </Grid>
                        {datas.image &&
                            <Grid item xs={12}>
                                <QuestionImagePreview
                                    imageSrc={`${process.env.REACT_APP_AWS_S3_URL}/${datas.image}`}
                                    description="사용자가 Q/A 문의에 첨부한 이미지를 확인합니다."
                                />
                            </Grid>
                        }
                        {replyHistory.length > 0 && (
                            <Grid item xs={12}>
                                <DetailSection
                                    title="기존 답변 이력"
                                    description="이전에 등록된 문의와 관리자 답변을 확인합니다."
                                >
                                    <Grid container spacing={1.5}>
                                        {replyHistory.map((item) => (
                                            <Grid item xs={12} key={item.qnaReplyId}>
                                                <FormControl fullWidth>
                                                    <TextField
                                                        label={item.writer ? `${datas.user?.username || '작성자'} ${formatDate(item.regTime)}` : `관리자 ${formatDate(item.regTime)}`}
                                                        id="replyContent"
                                                        name="replyContent"
                                                        multiline
                                                        minRows={2}
                                                        value={item.reply || ''}
                                                        size="small"
                                                        InputProps={{ readOnly: true }}
                                                    />
                                                </FormControl>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </DetailSection>
                            </Grid>
                        )}
                        <Grid item xs={12} sm={12} md={12}>
                            <DetailSection
                                title="답변 작성"
                                description="저장하면 Q/A 문의 목록으로 돌아갑니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        label="답변"
                                        id="reply"
                                        name="reply"
                                        multiline
                                        minRows={8}
                                        value={datas.reply || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                        disabled={saving}
                                        placeholder="고객에게 전달할 답변을 입력하세요."
                                    />
                                </FormControl>
                            </DetailSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={() => moveToList()}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
